import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function Step2MeetUp({ formData, updateFormData, onNext, onBack, meetUpEvents = [] }) {
  const today          = new Date().toISOString().split('T')[0];
  const upcomingEvents = meetUpEvents.filter(e => e.active && e.event_date >= today);

  const handleSelect = (event) => {
    updateFormData({
      meet_up_event_id:    event.id,
      meet_up_event_title: event.title,
      meet_up_location:    event.location,
      delivery_date:       event.event_date,
      delivery_time:       event.event_time ? event.event_time.slice(0, 5) : '',
      // Clear delivery address — meet-ups are pick-up only
      delivery_address:    '',
      delivery_city:       '',
      delivery_zip:        '',
      recipient_name:      formData.recipient_name || '',
    });
  };

  const isValid = !!formData.meet_up_event_id && upcomingEvents.some(e => e.id === formData.meet_up_event_id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-serif text-charcoal">Choose Your Meet-Up</h2>
        <p className="text-sage-500 font-sans">
          Fresh food, picked up in person. No delivery fee, no wait — just show up and eat.
        </p>
      </div>

      {upcomingEvents.length === 0 ? (
        <div className="bg-white rounded-3xl border border-sage-100 p-12 text-center space-y-3">
          <div className="w-14 h-14 bg-sage-100 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon name="Calendar" className="text-sage-400 text-2xl" />
          </div>
          <h3 className="font-serif text-xl text-charcoal">No upcoming meet-ups right now</h3>
          <p className="text-sage-400 text-sm">
            Check back soon — or{' '}
            <a href="mailto:orders@phoenixkabob.com" className="text-blush-400 font-bold hover:text-blush-500 transition-colors">
              contact us
            </a>{' '}
            to be notified when the next one is scheduled.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {upcomingEvents.map(event => {
            const isSelected = formData.meet_up_event_id === event.id;
            return (
              <motion.button
                key={event.id}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => handleSelect(event)}
                className={`relative text-left rounded-3xl p-6 border-2 transition-all duration-300 focus:outline-none w-full ${
                  isSelected
                    ? 'border-blush-300 bg-blush-50/30 shadow-lg ring-4 ring-blush-100'
                    : 'border-sage-100 bg-white hover:border-blush-200 hover:shadow-soft'
                }`}
              >
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-5 right-5 w-8 h-8 bg-blush-300 rounded-full flex items-center justify-center shadow-md"
                  >
                    <SafeIcon name="Check" className="text-white text-sm" />
                  </motion.div>
                )}

                <div className="flex items-start gap-5">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-blush-100' : 'bg-sage-100'
                  }`}>
                    <SafeIcon name="MapPin" className={`text-2xl ${isSelected ? 'text-blush-400' : 'text-sage-400'}`} />
                  </div>

                  <div className="space-y-1.5 flex-1">
                    <h3 className="font-serif text-xl text-charcoal">{event.title}</h3>
                    <p className="text-sm font-bold text-charcoal">{formatDate(event.event_date)}</p>
                    <p className="text-sm text-sage-500 flex items-center gap-1.5">
                      <SafeIcon name="Clock" className="text-xs shrink-0" />
                      Starting at {formatTime(event.event_time)}
                    </p>
                    <p className="text-sm text-sage-500 flex items-center gap-1.5">
                      <SafeIcon name="MapPin" className="text-xs shrink-0" />
                      {event.location}
                    </p>
                    {event.notes && (
                      <p className="text-xs text-sage-400 italic mt-1">{event.notes}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span className="text-xs bg-green-50 text-green-700 font-bold px-2.5 py-1 rounded-full border border-green-100">
                        No Delivery Fee
                      </span>
                      <span className="text-xs bg-blush-50 text-blush-600 font-bold px-2.5 py-1 rounded-full border border-blush-100">
                        Pick-Up Only
                      </span>
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      <NavButtons
        onNext={onNext}
        onBack={onBack}
        disabled={!isValid}
        nextLabel="Continue"
      />
    </motion.div>
  );
}
